import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/Landing.module.css';

const DEMO_URL = 'https://api.leadconnectorhq.com/widget/form/VcuKrz8mFUyNWJqURaF8';

const Landing = () => {
  const { user, isAdmin, isConsultant } = useAuth();
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const iframeRef = useRef(null);
  const demoLoadedRef = useRef(false);
  const [demoOpen, setDemoOpen] = useState(false);

  const openDemo = useCallback(() => {
    if (iframeRef.current && !demoLoadedRef.current) {
      iframeRef.current.src = DEMO_URL;
      demoLoadedRef.current = true;
    }
    setDemoOpen(true);
  }, []);

  const closeDemo = useCallback(() => setDemoOpen(false), []);

  useEffect(() => {
    document.body.style.overflow = demoOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [demoOpen]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeDemo(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeDemo]);

  // Reveal-on-scroll + count-up animations, ported 1:1 from the static design.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    const revealEls = Array.from(root.querySelectorAll('[data-reveal]'));
    let io;
    if (!('IntersectionObserver' in window)) {
      revealEls.forEach((e) => e.classList.add(styles.in));
    } else {
      io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add(styles.in);
            io.unobserve(en.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
      revealEls.forEach((e, i) => {
        e.style.transitionDelay = `${Math.min(i % 3, 2) * 80}ms`;
        io.observe(e);
      });
    }

    function animateCount(el) {
      const to = parseFloat(el.dataset.to);
      const pre = el.dataset.prefix || '';
      const suf = el.dataset.suffix || '';
      const dur = 1100;
      let start = null;
      function step(ts) {
        if (!start) start = ts;
        const p = Math.min((ts - start) / dur, 1);
        const val = Math.round(to * p);
        el.textContent = pre + val + suf;
        if (p < 1) requestAnimationFrame(step);
        else el.textContent = pre + to + suf;
      }
      requestAnimationFrame(step);
    }

    const countEls = Array.from(root.querySelectorAll('[data-to]'));
    let cio;
    if ('IntersectionObserver' in window) {
      cio = new IntersectionObserver((ents) => {
        ents.forEach((en) => {
          if (en.isIntersecting) {
            animateCount(en.target);
            cio.unobserve(en.target);
          }
        });
      }, { threshold: 0.6 });
      countEls.forEach((c) => cio.observe(c));
    } else {
      countEls.forEach((c) => {
        c.textContent = (c.dataset.prefix || '') + c.dataset.to + (c.dataset.suffix || '');
      });
    }

    return () => {
      if (io) io.disconnect();
      if (cio) cio.disconnect();
    };
  }, []);

  // Auto-hide the header on scroll-down (mobile only, same 560px breakpoint
  // as the stacked nav) so the buttons don't permanently eat screen space —
  // scrolling back up, or being near the top, reveals it again.
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return undefined;

    const HIDE_BREAKPOINT = 560;
    const REVEAL_THRESHOLD = 80;
    let lastY = window.scrollY;

    // No rAF batching here on purpose — this only does a couple of property
    // reads and a classList toggle per event, cheap enough to run directly,
    // and rAF-gating it would stall on backgrounded/occluded tabs.
    const update = () => {
      if (window.innerWidth > HIDE_BREAKPOINT) {
        header.classList.remove(styles.hidden);
        lastY = window.scrollY;
        return;
      }
      const y = window.scrollY;
      if (y <= REVEAL_THRESHOLD) {
        header.classList.remove(styles.hidden);
      } else if (y > lastY) {
        header.classList.add(styles.hidden);
      } else if (y < lastY) {
        header.classList.remove(styles.hidden);
      }
      lastY = y;
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div className={styles.page} ref={containerRef}>
      <header className={styles.header} ref={headerRef}>
        <div className={styles.nav}>
          <a href="#top"><img src="/images/landing/logo.png" alt="Selltrix" /></a>
          <div className={styles.r}>
            <a className={clsx(styles['btn-demo'], styles.demolink)} href="#" onClick={(e) => { e.preventDefault(); openDemo(); }}>Agendar demo</a>
            <a className={clsx(styles.btn, styles['btn-primary'], styles['btn-nav'], styles['btn-nav-cta'])} href="#planes">Empieza ahora</a>
            {user ? (
              <Link
                to={isAdmin ? '/admin' : isConsultant ? '/afiliados' : '/dashboard'}
                className={clsx(styles.btn, styles['btn-primary'], styles['btn-nav'])}
              >
                {isAdmin ? 'Panel Admin' : isConsultant ? 'Panel de Afiliados' : 'Ir al Dashboard'}
              </Link>
            ) : (
              <Link to="/login" className={styles.demolink} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <LogIn className={styles.ic} style={{ width: '16px', height: '16px', margin: 0 }} />
                Iniciar Sesión
              </Link>
            )}
          </div>
        </div>
      </header>

      <a id="top" />

      {/* HERO */}
      <section className={clsx(styles.hero, styles.section)}>
        <div className={styles.wrap}>
          <h1>Haz crecer tu negocio de Mercado Libre con <span className={styles.hl}>Inteligencia Artificial</span></h1>
          <p className={styles.sub}>La IA que encuentra tus productos ganadores, arma tus publicaciones profesionales en minutos y te muestra qué productos te dejan plata de verdad — todo en un solo lugar.</p>

          <div className={styles['vsl-box']}>
            <video
              src="/videos/landing.mp4"
              poster="/videos/landing-poster.jpg"
              controls
              playsInline
              preload="metadata"
            />
          </div>

          <div className={styles['cta-row']}>
            <a className={clsx(styles.btn, styles['btn-primary'], styles['btn-lg'])} href="#planes">Empieza ahora</a>
          </div>
          <div className={styles.microg}><svg className={styles.ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z"/><path d="M9 12l2 2 4-4"/></svg><b>7 días de garantía</b> · Cancela cuando quieras</div>
          <div className={styles['hero-proof']}><svg className={styles.ic} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3 6.5 7 .6-5.3 4.6 1.6 6.8L12 17l-6 3.5 1.6-6.8L2 9.1l7-.6L12 2z"/></svg>+100 vendedores ya crecen con Selltrix</div>
        </div>
      </section>

      {/* TRUST BAR */}
      <div className={styles.trustbar}>
        <div className={styles.wrap}>
          <div className={styles.trustitem}><span className={styles.tn}><span data-to="150" data-prefix="+">+0</span></span><span className={styles.tl}>cuentas asesoradas</span></div>
          <div className={styles.trustsep} />
          <div className={styles.trustitem}><span className={styles.tn}><span data-to="1" data-prefix="+$" data-suffix="M">+$0M</span></span><span className={styles.tl}>USD vendidos</span></div>
          <div className={styles.trustsep} />
          <div className={styles.trustitem}><span className={styles.tn}><span data-to="5">0</span></span><span className={styles.tl}>años vendiendo</span></div>
          <div className={styles.trustsep} />
          <div className={clsx(styles.trustitem, styles.plat)}><span className={styles.tn}><svg className={styles.ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4h12v3a6 6 0 0 1-12 0V4z"/><path d="M6 5H3v2a3 3 0 0 0 3 3M18 5h3v2a3 3 0 0 1-3 3M9 20h6M12 13v4"/></svg>Sponsor Platinum</span><span className={styles.tl}>Mercado Libre Experience</span></div>
        </div>
      </div>

      {/* PROBLEMAS: ANTES vs DESPUES */}
      <section className={styles.section}>
        <div className={styles.wrap}>
          <h2 data-reveal className={clsx(styles['sec-title'], styles.reveal)}>De vender a la suerte… a vender con datos</h2>
          <p data-reveal className={clsx(styles['sec-sub'], styles.reveal)}>Así cambia tu día a día en Mercado Libre con Selltrix.</p>

          <div data-reveal className={clsx(styles.ba, styles.reveal)}>
            <div className={clsx(styles['ba-col'], styles.before)}>
              <div className={styles['ba-head']}>😫 Sin Selltrix</div>
              <ul>
                <li><span className={styles.x}>✕</span> Eliges qué vender por intuición y le metes plata a productos que no se mueven.</li>
                <li><span className={styles.x}>✕</span> Pierdes 3 o 4 horas armando una publicación… sin saber si va a posicionar.</li>
                <li><span className={styles.x}>✕</span> Vendes, pero no sabes qué está frenando tu cuenta ni qué mover.</li>
                <li><span className={styles.x}>✕</span> Ves la facturación, pero no lo que de verdad te queda.</li>
              </ul>
            </div>
            <div className={clsx(styles['ba-col'], styles.after)}>
              <div className={styles['ba-head']}>🚀 Con Selltrix</div>
              <ul>
                <li><span className={styles.c}>✓</span> Analizas cualquier nicho con datos reales y sabes qué vender antes de invertir.</li>
                <li><span className={styles.c}>✓</span> Subes una foto y la IA te arma publicaciones profesionales en minutos.</li>
                <li><span className={styles.c}>✓</span> Analiza tu cuenta en segundos y te dice exactamente qué hacer para mejorar.</li>
                <li><span className={styles.c}>✓</span> Tienes tu utilidad real en vivo: sabes qué producto te deja plata y cuál te la quita.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MIRALO POR DENTRO — showcase alternado */}
      <section className={styles.section} style={{ background: 'var(--soft)', overflow: 'hidden' }}>
        <div className={styles.wrap}>
          <h2 data-reveal className={clsx(styles['sec-title'], styles.reveal)}>Míralo por dentro</h2>
          <p data-reveal className={clsx(styles['sec-sub'], styles.reveal)}>Datos reales, decisiones claras — todo en una sola plataforma con IA.</p>

          <div className={styles.show}>
            <div data-reveal className={clsx(styles['show-row'], styles.reveal)}>
              <div className={styles['show-txt']}>
                <span className={styles['show-tag']}>Búsqueda de productos</span>
                <h3>Saber qué vender antes de invertir</h3>
                <p>Analiza cualquier nicho con datos reales de Mercado Libre —demanda, competencia y facturación— y te da un veredicto claro.</p>
              </div>
              <div className={styles['show-img']}><div className={styles.mock}><div className={styles['mock-bar']}><i /><i /><i /><em>selltrix.ai/research</em></div><img src="/images/landing/research-mock.jpg" alt="Búsqueda de productos" /></div></div>
            </div>

            <div data-reveal className={clsx(styles['show-row'], styles.rev, styles.reveal)}>
              <div className={styles['show-txt']}>
                <span className={styles['show-tag']}>Publicaciones con IA</span>
                <h3>7 infografías profesionales desde una foto</h3>
                <p>Subes una imagen de tu producto y la IA genera las infografías y los textos optimizados en minutos. Tú solo revisas y publicas.</p>
              </div>
              <div className={styles['show-img']}><div className={styles.mock}><div className={styles['mock-bar']}><i /><i /><i /><em>selltrix.ai/publications</em></div><img src="/images/landing/publications-mock.jpg" alt="Publicaciones con IA" /></div></div>
            </div>

            <div data-reveal className={clsx(styles['show-row'], styles.reveal)}>
              <div className={styles['show-txt']}>
                <span className={styles['show-tag']}>Analytics</span>
                <h3>Entiende por qué suben o bajan tus ventas</h3>
                <p>No solo el qué —el porqué. Selltrix te muestra qué cambió en tu cuenta y qué hacer para recuperar o impulsar tus ventas.</p>
              </div>
              <div className={styles['show-img']}><div className={styles.mock}><div className={styles['mock-bar']}><i /><i /><i /><em>selltrix.ai/analytics</em></div><img src="/images/landing/analytics-mock.jpg" alt="Analytics" /></div></div>
            </div>

            <div data-reveal className={clsx(styles['show-row'], styles.rev, styles.reveal)}>
              <div className={styles['show-txt']}>
                <span className={styles['show-tag']}>Rentabilidad real</span>
                <h3>Tu utilidad de verdad, en vivo</h3>
                <p>Comisiones, envíos, publicidad… y lo que casi nadie mide: tu <b>costo de producto</b>, tus <b>gastos operativos</b> y tus <b>impuestos</b>. Selltrix junta todo y te dice cuánto te queda de verdad.</p>
              </div>
              <div className={styles['show-img']}><div className={styles.mock}><div className={styles['mock-bar']}><i /><i /><i /><em>selltrix.ai/dashboard</em></div><img src="/images/landing/dashboard-mock.jpg" alt="Rentabilidad real" /></div></div>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.strip}>
        <div className={styles.wrap}>
          <h3 data-reveal className={styles.reveal}>Deja de adivinar. Empieza a vender con datos.</h3>
          <a className={clsx(styles.btn, styles['btn-primary'], styles['btn-lg'])} href="#planes">Empieza ahora</a>
        </div>
      </div>

      {/* PRUEBA SOCIAL */}
      <section className={styles.section}>
        <div className={styles.wrap}>
          <h2 data-reveal className={clsx(styles['sec-title'], styles.reveal)}>Vendedores que ya venden con datos, no a la suerte</h2>

          <div data-reveal className={clsx(styles.authbar, styles.reveal)}>
            <div>Detrás de Selltrix hay años vendiendo en Mercado Libre:</div>
            <div className={styles.nums}>+$1M USD vendidos · +150 cuentas asesoradas · Vendedor Platinum hace 5 años</div>
            <div className={styles.plat}><svg className={styles.ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4h12v3a6 6 0 0 1-12 0V4z"/><path d="M6 5H3v2a3 3 0 0 0 3 3M18 5h3v2a3 3 0 0 1-3 3M9 20h6M12 13v4"/></svg>Sponsor Platinum del Mercado Libre Experience 2026</div>
            <div className={styles.sponswrap}><img className={styles.sponsimg} src="/images/landing/sponsor.jpg" alt="Sponsor Platinum Mercado Libre Experience" /><span className={styles.spot} /><span className={styles['spot-tag']}>👋 Nosotros</span></div>
          </div>

          <div data-reveal className={clsx(styles.results, styles.reveal)}>
            <div className={styles.case}>
              <div className={styles['case-shot']}><img src="/images/landing/case-1.jpg" alt="Resultado en Selltrix — Guillermo" /></div>
              <div className={styles['case-txt']}>
                <div className={styles['case-head']}><span className={styles['case-name']}>Guillermo H.</span><span className={styles.rstars}>★★★★★</span></div>
                <p>Pasó de <b>$5M</b> a más de <b>$40M al mes</b> usando Selltrix para encontrar sus productos ganadores y optimizar sus publicaciones.</p>
              </div>
            </div>
            <div className={styles.case}>
              <div className={styles['case-shot']}><img src="/images/landing/case-2.jpg" alt="Resultado en Selltrix — Angie" /></div>
              <div className={styles['case-txt']}>
                <div className={styles['case-head']}><span className={styles['case-name']}>Angie L.</span><span className={styles.rstars}>★★★★★</span></div>
                <p>En un solo mes pasó de <b>$800K</b> a más de <b>$5M</b> en ventas, tomando cada decisión con datos en lugar de a la suerte.</p>
              </div>
            </div>
          </div>
          <div data-reveal className={clsx(styles.tgrid, styles.reveal)}>
            <div className={styles.tcard}>
              <div className={styles.stars}>★★★★★</div>
              <p>"El software es una belleza. El tiempo que se ahorra uno analizando productos, generando infografías y título no tiene precio. Además, tener el dashboard donde uno pueda revisar todas las métricas del negocio en el mismo lugar permite ver dónde están las oportunidades de mejora fácilmente."</p>
              <div className={styles.who}>Antonio C. <span>· Vendedor de Mercado Libre</span></div>
            </div>
            <div className={styles.tcard}>
              <div className={styles.stars}>★★★★★</div>
              <p>"Los que llevamos tiempo vendiendo en Mercado Libre valoramos muchísimo este software. Antes, saber cuánto nos quedaba de ganancia implicaba cruzar archivos y procesos manuales. Hoy poder ver rentabilidad real, conversión, optimizar títulos e imágenes desde un solo lugar nos ahorra muchísimo trabajo."</p>
              <div className={styles.who}>Cristian O. <span>· Vendedor de Mercado Libre</span></div>
            </div>
          </div>
          <div className={styles.center} style={{ marginTop: '30px' }}>
            <a className={clsx(styles.btn, styles['btn-primary'], styles['btn-lg'])} href="#planes">Quiero empezar</a>
          </div>
        </div>
      </section>

      {/* PRECIOS */}
      <a id="planes" />
      <section className={styles.section} style={{ background: 'var(--soft)' }}>
        <div className={styles.wrap}>
          <h2 data-reveal className={clsx(styles['sec-title'], styles.reveal)}>Elige el plan para tu tamaño</h2>
          <p className={styles['sec-sub']}>Todos incluyen acceso completo a la plataforma. Sin permanencia — cancela cuando quieras.</p>
          <div data-reveal className={clsx(styles['launch-banner'], styles.reveal)}><svg className={styles.ic} viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z"/></svg>Precios de lanzamiento por tiempo limitado — hasta los primeros <b>200 usuarios</b>. Después suben.</div>

          <div data-reveal className={clsx(styles.plans, styles.reveal)}>
            <div className={styles.plan}>
              <div className={styles.pn}>Emprendedor</div>
              <div className={styles.pp}>$29 <small>USD/mes</small></div>
              <div className={styles.pd}>Para vendedores de hasta 100 ventas al mes.</div>
              <ul>
                <li><span className={styles.ck}>✓</span> Acceso completo a la plataforma</li>
                <li><span className={styles.ck}>✓</span> Análisis de rentabilidad y cuenta</li>
                <li><span className={styles.ck}>✓</span> Búsqueda de productos con datos</li>
                <li><span className={styles.ck}>✓</span> 7 generaciones de imágenes al mes</li>
                <li><span className={styles.ck}>✓</span> Soporte por correo</li>
              </ul>
              <div className={styles['plan-actions']}>
                <a className={clsx(styles.btn, styles['btn-primary'], styles['btn-buy'])} data-plan="emprendedor" href="/register">Comprar</a>
                <a className={clsx(styles.btn, styles['btn-ghost'], styles['btn-demo'])} href="#" onClick={(e) => { e.preventDefault(); openDemo(); }}>Agendar demo</a>
              </div>
            </div>

            <div className={styles.plan}>
              <div className={styles.pn}>Pro</div>
              <div className={styles.pp}>$69 <small>USD/mes</small></div>
              <div className={styles.pd}>Para operaciones en crecimiento de hasta 500 ventas al mes.</div>
              <ul>
                <li><span className={styles.ck}>✓</span> Todo lo del plan Emprendedor</li>
                <li><span className={styles.ck}>✓</span> 15 generaciones de imágenes al mes</li>
                <li><span className={styles.ck}>✓</span> Más búsquedas de producto</li>
                <li><span className={styles.ck}>✓</span> Soporte prioritario</li>
              </ul>
              <div className={styles['plan-actions']}>
                <a className={clsx(styles.btn, styles['btn-primary'], styles['btn-buy'])} data-plan="pro" href="/register">Comprar</a>
                <a className={clsx(styles.btn, styles['btn-ghost'], styles['btn-demo'])} href="#" onClick={(e) => { e.preventDefault(); openDemo(); }}>Agendar demo</a>
              </div>
            </div>

            <div className={styles.plan}>
              <div className={styles.pn}>Elite</div>
              <div className={styles.pp}>$129 <small>USD/mes</small></div>
              <div className={styles.pd}>Para cuentas de alta operación, 1000 ventas al mes o más.</div>
              <ul>
                <li><span className={styles.ck}>✓</span> Todo lo del plan Pro</li>
                <li><span className={styles.ck}>✓</span> 30 generaciones de imágenes al mes</li>
                <li><span className={styles.ck}>✓</span> Máximas búsquedas de producto</li>
                <li><span className={styles.ck}>✓</span> Soporte dedicado</li>
              </ul>
              <div className={styles['plan-actions']}>
                <a className={clsx(styles.btn, styles['btn-primary'], styles['btn-buy'])} data-plan="elite" href="/register">Comprar</a>
                <a className={clsx(styles.btn, styles['btn-ghost'], styles['btn-demo'])} href="#" onClick={(e) => { e.preventDefault(); openDemo(); }}>Agendar demo</a>
              </div>
            </div>
          </div>

          <div className={styles['guarantee-line']}><svg className={styles.ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z"/><path d="M9 12l2 2 4-4"/></svg>Todos los planes con <b>7 días de garantía</b>. Si no es para ti, te devolvemos tu dinero.</div>
          <div className={styles['demo-cta']} style={{ marginTop: '22px' }}>
            <div style={{ marginBottom: '12px' }}>¿Vendes más de 100 al mes y quieres verlo aplicado a tu cuenta?</div>
            <a className={clsx(styles.btn, styles['btn-ghost'], styles['btn-demo'])} href="#" onClick={(e) => { e.preventDefault(); openDemo(); }}>Agendar demo</a>
          </div>
        </div>
      </section>

      {/* GARANTIA */}
      <section className={styles.section}>
        <div className={styles.wrap}>
          <div data-reveal className={clsx(styles.gbox, styles.reveal)}>
            <div className={styles.shield}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style={{ width: '52px', height: '52px' }}><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z"/><path d="M9 12l2 2 4-4"/></svg></div>
            <h3>Pruébalo 7 días. Sin riesgo.</h3>
            <p>Entra, conecta tu cuenta y usa Selltrix con tus productos reales. Si en 7 días sientes que no es para ti, nos escribes y te devolvemos tu dinero. Sin preguntas.</p>
            <p className={styles['gbox-strong']}>El riesgo es nuestro, no tuyo.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className={styles.section} style={{ background: 'var(--soft)' }}>
        <div className={styles.wrap}>
          <h2 data-reveal className={clsx(styles['sec-title'], styles.reveal)}>Preguntas frecuentes</h2>
          <div data-reveal className={clsx(styles.faq, styles.reveal)}>
            <details className={styles.qa}><summary>¿Necesito saber de tecnología para usarlo?</summary><p>No. Selltrix está pensado para vendedores, no para técnicos. Conectas tu cuenta y la IA hace el trabajo pesado por ti.</p></details>
            <details className={styles.qa}><summary>¿Cómo conecto mi cuenta de Mercado Libre?</summary><p>Se conecta a Mercado Libre directamente: autorizas la sincronización una única vez y listo.</p></details>
            <details className={styles.qa}><summary>¿Puedo cambiar de plan o cancelar cuando quiera?</summary><p>Sí. No hay permanencia. Subes, bajas o cancelas tu plan cuando quieras.</p></details>
            <details className={styles.qa}><summary>¿La IA de verdad arma las publicaciones sola?</summary><p>Sí. Subes una foto de tu producto y Selltrix genera las infografías y los textos optimizados en minutos. Tú solo revisas y publicas.</p></details>
            <details className={styles.qa}><summary>¿Y si no me funciona?</summary><p>Tienes 7 días de garantía. Si sientes que no es para ti, te devolvemos tu dinero sin preguntas.</p></details>
          </div>
          <div className={styles.center} style={{ marginTop: '34px' }}>
            <a className={clsx(styles.btn, styles['btn-primary'], styles['btn-lg'])} href="#planes">Empieza ahora</a>
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <footer className={styles.footer}>
        <h2>Deja de vender a la suerte</h2>
        <p className={styles.sub}>Empieza a tomar cada decisión de tu negocio con datos e inteligencia artificial. En minutos estás dentro.</p>
        <a className={clsx(styles.btn, styles['btn-primary'], styles['btn-lg'])} href="#planes">Comprar ahora</a>
        <div className={styles.fnote}><svg className={styles.ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z"/><path d="M9 12l2 2 4-4"/></svg>7 días de garantía · Cancela cuando quieras</div>
        <img src="/images/landing/footer-badge.png" alt="Selltrix" style={{ filter: 'brightness(0) invert(1)' }} />
      </footer>

      <a id="agendar" />

      {/* DEMO MODAL (formulario GHL) */}
      <div
        className={clsx(styles['demo-modal'], demoOpen && styles.open)}
        onClick={(e) => { if (e.target === e.currentTarget) closeDemo(); }}
      >
        <div className={styles['demo-sheet']}>
          <div className={styles['dm-head']}>
            <div>
              <h3>Agenda tu demo</h3>
              <p>Déjanos tus datos y nuestro equipo te contacta.</p>
            </div>
            <button type="button" className={styles['dm-x']} onClick={closeDemo}>✕</button>
          </div>
          <iframe ref={iframeRef} title="Agendar demo" />
        </div>
      </div>
    </div>
  );
};

export default Landing;